import { ConfigurationProvider } from "../../../lib/infrastructure/configuration/ConfigurationProvider";
import { HttpClient } from "../../../lib/infrastructure/http/HttpClient";
import { Space } from "../models/Space";
import { SpaceByIdResponseDTO } from "../models/api/SpaceByIdResponseDTO";
import { SpaceContentsResponseDTO } from "../models/api/SpaceContentsResponseDTO";
import { SpaceCreateRequestDTO } from "../models/api/SpaceCreateRequestDTO";
import { SpaceCreateResponseDTO } from "../models/api/SpaceCreateResponseDTO";
import { SpaceResponseDTO } from "../models/SpaceResponseDTO";
import { SpaceSummary, SpaceSummaryEnvelope } from "../models/api/SpaceSummaryDTO";
import { SpaceUpdateRequestDTO } from "../models/api/UpdateSpaceRequestDTO";
import { UrlUtility } from "../../../lib/utils/UrlUtility";

const Route = (path: string) => {
  return UrlUtility.join(
    ConfigurationProvider.getConfiguration().App.BackendUrl,
    path
  );
};

/** What the space screens need; the screens take it as a prop so tests can pass a fake. */
export interface ISpaceService {
  getSpaces(): Promise<Space[]>;
  getById(spaceId: string): Promise<Space>;
  deleteSpace(spaceId: string): Promise<void>;
  updateSpace(title?: string, description?: string, projectId?: string, isHighlighted?: boolean, id?: string): Promise<SpaceResponseDTO>;
  createSpace(requestDTO: SpaceCreateRequestDTO): Promise<SpaceCreateResponseDTO>;
  getSpaceContent(id: string): Promise<SpaceContentsResponseDTO>;
  getSpaceSummaries(): Promise<SpaceSummary[]>;
}

export class SpaceService implements ISpaceService {
  async getSpaces(): Promise<Space[]> {
    return HttpClient.sessionRequest({
      method: "GET",
      url: Route("/api/Spaces"),
    })
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        console.log(err);
        throw err;
      });
  }

  async getById(spaceId: string): Promise<Space> {
    return HttpClient.sessionRequest<SpaceByIdResponseDTO>({
      method: "GET",
      url: Route(`/api/Spaces/${spaceId}`),
    })
      .then((res) => {
        const mappedSpace: Space = {
          description: res.data.description,
          title: res.data.title,
          spaceId: res.data.spaceId,
          externalId: res.data.externalId,
          projectId: res.data.projectId,
          isHighlighted: res.data.isHighlighted
        };
        return mappedSpace;
      })
      .catch((err) => {
        throw err
      });
  }

  async deleteSpace(spaceId: string): Promise<void> {
    return HttpClient.sessionRequest({
      method: "DELETE",
      url: Route(`/api/Spaces/${spaceId}`),
    })
      .then(() => { })
      .catch((error) => {
        throw error;
      });
  }

  updateSpace(
    title?: string,
    description?: string,
    projectId?: string,
    isHighlighted?: boolean,
    id?: string
  ): Promise<SpaceResponseDTO> {

    const requestDTO: SpaceUpdateRequestDTO = {
      description: description,
      projectId: projectId,
      title: title,
      isHighlighted : isHighlighted || false
    };

    return HttpClient.sessionRequest({
      method: "PUT",
      url: Route(`/api/Spaces/${id}`),
      data: requestDTO,
    })
      .then((res) => {
        return res.data;
      })
      .catch((error) => {
        throw error;
      });
  }

  async createSpace(
    requestDTO: SpaceCreateRequestDTO
  ): Promise<SpaceCreateResponseDTO> {
    return HttpClient.sessionRequest({
      method: "POST",
      url: Route("/api/Spaces"),
      data: requestDTO,
    })
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        console.log(err);
        throw err;
      });
  }

  async getSpaceContent(id: string): Promise<SpaceContentsResponseDTO> {
    return HttpClient.sessionRequest({
      method: "GET",
      url: Route(`/api/Spaces/${id}/contents`),
    })
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        console.log(err);
        throw err;
      });
  }

  /**
   * Page count and push policy of every space the caller reads, in one call (`GET api/agentic/v1/spaces`, the facade the orbital CLI
   * uses: the same readable-spaces filter as `GET api/Spaces`, visible pages only). The Spaces home and the settings read it for
   * their figures; a server without the facade simply shows none.
   */
  async getSpaceSummaries(): Promise<SpaceSummary[]> {
    const res = await HttpClient.sessionRequest<SpaceSummaryEnvelope>({
      method: "GET",
      url: Route("/api/agentic/v1/spaces"),
      headers: { "X-Orbital-Client": "ark-web" },
    });
    const envelope = res.data;
    if (!envelope) throw new Error("Empty response from the server.");
    if (envelope.error) throw new Error(envelope.error.message || envelope.error.code);
    return (envelope.data ?? []).map((s) => ({
      id: s.id,
      pageCount: typeof s.pageCount === "number" ? s.pageCount : undefined,
      pushPolicy: s.pushPolicy === "propose" ? "propose" : "direct",
    }));
  }
}

let defaultInstance: SpaceService | undefined;

/** The shared instance the screens use when no service prop is passed. */
export function spaceService(): ISpaceService {
  if (!defaultInstance) defaultInstance = new SpaceService();
  return defaultInstance;
}
